// Drain the offline submission queue. Called when the browser regains
// connectivity (online event) or when Background Sync fires the
// `submission-drain` tag. Per-record state machine:
//
//   queued → uploading → verifying → done
//                    ↘ failed (with retry budget)

import { listPending, update } from './submissionQueue.js';
import {
  signUpload,
  uploadToCloudinary,
  verifyPhoto,
  verifyMultiPhoto,
  verifyText,
  newSubmissionId,
} from './api.js';
import { compressForUpload } from './imageCompression.js';

const MAX_ATTEMPTS = 5;

let draining = false;

export async function drainOnce({ onProgress } = {}) {
  if (draining) return { drained: 0, skipped: 'already_draining' };
  draining = true;
  let drained = 0;
  let failed = 0;
  try {
    const items = await listPending();
    for (const item of items) {
      if (item.attempts >= MAX_ATTEMPTS) {
        await update(item.id, { status: 'failed', last_error: 'max_attempts' });
        failed += 1;
        continue;
      }
      try {
        const result = await runSingle(item);
        await update(item.id, { status: 'done', result, last_error: null });
        drained += 1;
      } catch (err) {
        await update(item.id, {
          status: 'queued',
          attempts: (item.attempts || 0) + 1,
          last_error: err.message || String(err),
        });
        failed += 1;
      }
      if (onProgress) onProgress({ drained, failed });
    }
    return { drained, failed };
  } finally {
    draining = false;
  }
}

async function runSingle(item) {
  const submissionId = item.submission_id || newSubmissionId();
  await update(item.id, { status: 'uploading', submission_id: submissionId });

  // ---- Photo / multi: sign + upload each blob to Cloudinary -----
  let photoUrl = null;
  let photoUrls = null;
  let exif = null;
  let exifList = null;
  if (item.submission_type === 'photo' || item.submission_type === 'photo_and_text') {
    const file = item.files?.[0];
    if (!file) throw new Error('queue_record_missing_file');
    const signed = await signUpload({
      tripId: item.trip_id,
      challengeId: item.challenge_id,
      submissionId,
    });
    // Compress before upload (queue records may have been enqueued
    // before compression-on-pick landed; we compress at drain time to
    // be safe). EXIF preserved.
    const compressed = await compressForUpload(blobToFile(file));
    const uploaded = await uploadToCloudinary({
      file: compressed,
      signed,
    });
    photoUrl = uploaded.secure_url;
    exif = uploaded.image_metadata || null;
  } else if (item.submission_type === 'multi_photo') {
    photoUrls = [];
    exifList = [];
    for (let i = 0; i < (item.files || []).length; i += 1) {
      const file = item.files[i];
      const signed = await signUpload({
        tripId: item.trip_id,
        challengeId: item.challenge_id,
        submissionId: `${submissionId}_${i}`,
      });
      const compressed = await compressForUpload(blobToFile(file));
      const uploaded = await uploadToCloudinary({
        file: compressed,
        signed,
      });
      photoUrls.push(uploaded.secure_url);
      exifList.push(uploaded.image_metadata || null);
    }
  }

  // ---- Verifier round-trip --------------------------------------
  await update(item.id, { status: 'verifying' });

  const common = {
    tripId: item.trip_id,
    challengeId: item.challenge_id,
    submissionId,
    location: item.claimed_location || null,
    timestamp: item.timestamp_claimed,
    testMode: !!item.test_mode,
  };

  if (item.submission_type === 'photo') {
    return await verifyPhoto({ ...common, photoUrl, exif });
  }
  if (item.submission_type === 'photo_and_text') {
    // No combined verifier endpoint exists yet — fall back to photo verify
    // and pass the user's text in the verifier prompt context. Phase 8
    // polish: dedicated combined verifier.
    return await verifyPhoto({ ...common, photoUrl, exif });
  }
  if (item.submission_type === 'multi_photo') {
    return await verifyMultiPhoto({ ...common, photoUrls, exifList });
  }
  if (item.submission_type === 'text') {
    return await verifyText({ ...common, text: item.text || '' });
  }
  throw new Error(`unsupported_submission_type:${item.submission_type}`);
}

function blobToFile(stored) {
  // Records can hold either a Blob (from `new Blob([...])`) or a File
  // (from <input type="file">). Both have arrayBuffer() and type.
  if (stored?.blob instanceof Blob) {
    return new File([stored.blob], stored.name || 'photo.jpg', {
      type: stored.type || stored.blob.type || 'image/jpeg',
    });
  }
  if (stored instanceof File) return stored;
  if (stored instanceof Blob) {
    return new File([stored], 'photo.jpg', { type: stored.type || 'image/jpeg' });
  }
  throw new Error('queue_record_invalid_file');
}
